// Notification preferences for team members (Settings > Notifications): alert categories and quiet
// hours. State lives in PreferencesController; rendering in NotificationsPage.tsx.
//
// Staff notifications are in-app and bcio.chat only. Sidetone never asks staff for a mobile number,
// never texts a verification code and never sends SMS alerts, so the controller has no way to. The
// retired SMS disclosure and helpers below stay exported so existing imports keep compiling.
import { type MessagingClient, type NotificationPreferences, isAbortError, isApiRequestError } from '../../../lib/api.ts';
import { describeError } from '../../../lib/errorCopy.ts';
import { type MessagingGate, REASON_FALLBACK, readPreferences } from '../../messaging/model.ts';

/** Shown on Settings > Notifications: where alerts go, and the only allowed second factor. */
export const STAFF_NOTIFICATION_POLICY = 'Staff notifications are delivered in-app and via bcio.chat only. Phone numbers (SMS) and email verification codes are disabled. For account security, only authenticator app (TOTP) two-step verification is allowed.';

export const STAFF_SMS_DISCLOSURE_VERSION = '2026-10-22-staff-alerts-v1';

/** Retired: the consent text the SMS alerts opt-in showed, paragraph by paragraph. No longer rendered. */
export const STAFF_SMS_DISCLOSURE: readonly string[] = [
  'By turning on SMS alerts and confirming the code we text you, you agree to receive automated text messages from Brandcore’s Sidetone at this mobile number about the alerts you choose: missed calls, voicemail and system alerts.',
  'Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help.',
  'SMS alerts are optional and aren’t required to use Sidetone. We do not share mobile information with third parties for promotional or marketing purposes.',
];

export const SMS_TERMS_URL = 'https://brandcore.io/terms/#sms';
export const PRIVACY_URL = 'https://brandcore.io/privacy/';

export const CATEGORY_LABEL: Record<keyof NotificationPreferences['categories'], { label: string; hint: string }> = {
  call_missed: { label: 'Missed calls', hint: 'A call to you or your queue wasn’t answered.' },
  voicemail: { label: 'Voicemail', hint: 'Someone left you a voicemail.' },
  system_alerts: { label: 'Workspace alerts', hint: 'Sidetone service problems and when they’re fixed.' },
};

export const CODE_PATTERN = /^\d{6}$/;

/** Digits only, at most six: pasted codes with spaces or dashes still work. */
export function normalizeCode(input: string): string {
  return input.replace(/\D/g, '').slice(0, 6);
}

export type AlertState = 'off' | 'unverified' | 'verified' | 'verified_off';

/** Where the member is in the flow, from the server's record alone. */
export function alertState(prefs: NotificationPreferences): AlertState {
  const verified = !!prefs.sms_verified_at && !!prefs.sms_e164;
  if (verified) return prefs.sms_enabled ? 'verified' : 'verified_off';
  return prefs.sms_e164 || prefs.sms_enabled ? 'unverified' : 'off';
}

export type VerifyBlock = { blocked: false } | { blocked: true; code: string; reason: string };

/**
 * Retired with the SMS opt-in (nothing offers "Send verification code" any more). The capability's
 * own sentence came first, so a pending 10DLC registration read exactly as the server stated it.
 */
export function verifyBlock(input: { gate: MessagingGate; canManage: boolean; agreed: boolean; numberValid: boolean; busy: boolean }): VerifyBlock {
  const { gate } = input;
  if (gate.state !== 'enabled') return { blocked: true, code: ('reasonCode' in gate && gate.reasonCode) || gate.state, reason: gate.detail };
  if (!input.canManage) return { blocked: true, code: 'no_notifications_permission', reason: 'Your role doesn’t include managing your own notifications. Ask an admin if you need it.' };
  if (!input.numberValid) return { blocked: true, code: 'invalid_e164', reason: 'Enter your mobile number first.' };
  if (!input.agreed) return { blocked: true, code: 'consent_required', reason: 'Tick the box to agree to the SMS alerts terms above.' };
  if (input.busy) return { blocked: true, code: 'busy', reason: 'Working…' };
  return { blocked: false };
}

export function verifyErrorText(error: unknown, gate: MessagingGate): string {
  if (isApiRequestError(error)) {
    if (error.code === 'messaging_disabled' || error.code === 'campaign_not_approved' || error.code === 'number_not_assigned') {
      return gate.state !== 'enabled' && gate.detail ? gate.detail : REASON_FALLBACK[error.code];
    }
    if (error.code === 'invalid_code') return 'That code isn’t right or has expired. Check it, or send a new code.';
    if (error.code === 'rate_limited' || error.status === 429) return 'Too many codes were requested. Wait an hour, then send a new code.';
    if (error.code === 'invalid_e164') return 'That number isn’t a valid mobile number.';
    if (error.outcomeUnknown) return 'The server didn’t answer. Check whether a code arrived before sending another.';
  }
  return describeError(error, 'Something went wrong. Try again.').title;
}

/** Quiet hours: both times set and different. */
export function quietHoursProblem(q: NotificationPreferences['quiet_hours']): string | null {
  if (!q) return null;
  if (!/^\d{2}:\d{2}$/.test(q.start) || !/^\d{2}:\d{2}$/.test(q.end)) return 'Set both a start and an end time.';
  if (q.start === q.end) return 'Start and end can’t be the same time.';
  if (!q.timezone) return 'Choose a time zone.';
  return null;
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function timeZones(current: string): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  let zones: string[] = [];
  try {
    zones = supported ? supported('timeZone') : [];
  } catch {
    zones = [];
  }
  return [...new Set([current, ...zones, 'UTC'])];
}

export type Busy = null | 'load' | 'save';

export interface PreferencesSnapshot {
  status: 'loading' | 'ready' | 'error';
  prefs: NotificationPreferences | null;
  loadError: ReturnType<typeof describeError> | null;
  busy: Busy;
  /** What the last action did, for the status line. */
  notice: string | null;
  actionError: string | null;
}

export class PreferencesController {
  private snapshot: PreferencesSnapshot = { status: 'loading', prefs: null, loadError: null, busy: 'load', notice: null, actionError: null };
  private readonly listeners = new Set<() => void>();

  constructor(private readonly client: MessagingClient) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): PreferencesSnapshot => this.snapshot;

  private set(patch: Partial<PreferencesSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of [...this.listeners]) listener();
  }

  async load(): Promise<void> {
    this.set({ status: this.snapshot.prefs ? 'ready' : 'loading', busy: this.snapshot.busy ?? 'load', loadError: null });
    try {
      const prefs = readPreferences(await this.client.preferences());
      if (!prefs) {
        this.set({ status: 'error', busy: null, loadError: { title: 'The server sent notification settings this version can’t read', detail: '', code: 'invalid_response', requestId: null, retryable: true } });
        return;
      }
      this.set({ status: 'ready', prefs, busy: null });
    } catch (error) {
      if (isAbortError(error)) return;
      this.set({ status: 'error', busy: null, loadError: describeError(error, 'Couldn’t load your notification settings') });
    }
  }

  private async put(next: NotificationPreferences): Promise<NotificationPreferences> {
    const saved = readPreferences(await this.client.savePreferences(next));
    return saved ?? next;
  }

  /** Alert categories and quiet hours; the rest of the record is sent back as the server last returned it. */
  async saveChoices(categories: NotificationPreferences['categories'], quietHours: NotificationPreferences['quiet_hours']): Promise<boolean> {
    const prefs = this.snapshot.prefs;
    if (!prefs || this.snapshot.busy) return false;
    this.set({ busy: 'save', actionError: null, notice: null });
    try {
      const saved = await this.put({ ...prefs, categories, quiet_hours: quietHours });
      this.set({ prefs: saved, busy: null, notice: 'Alert choices saved.' });
      return true;
    } catch (error) {
      this.set({ busy: null, actionError: describeError(error, 'Couldn’t save your alert choices').title });
      return false;
    }
  }
}

